import React from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  TargetedEvent,
  // eslint-disable-next-line no-restricted-imports
  TouchableOpacity as RNTouchableOpacity,
  TouchableOpacityProps,
  ViewProps,
} from 'react-native'
import { TouchableOpacity as GestureTouchableOpacity } from 'react-native-gesture-handler'
import styled from 'styled-components/native'

import { accessibilityAndTestId } from 'libs/accessibilityAndTestId'
import { useHandleFocus } from 'libs/hooks/useHandleFocus'
import { touchableFocusOutline } from 'ui/theme/customFocusOutline/touchableFocusOutline'

type Props = TouchableOpacityProps & {
  tabIndex?: ViewProps['tabIndex']
  shouldUseGestureHandler?: boolean
}
type StyledProps = { unselectable?: boolean; isFocus?: boolean }

export function TouchableOpacity({
  shouldUseGestureHandler = false,
  disabled,
  onFocus,
  onBlur,
  children,
  accessibilityLabel,
  testID,
  ...props
}: Props) {
  const { onFocus: onFocusDefault, onBlur: onBlurDefault, isFocus } = useHandleFocus()

  const isWeb = Platform.OS === 'web'
  const onStyledFocus = (e: NativeSyntheticEvent<TargetedEvent>) => {
    onFocusDefault()
    onFocus?.(e)
  }
  const onStyledBlur = (e: NativeSyntheticEvent<TargetedEvent>) => {
    onBlurDefault()
    onBlur?.(e)
  }

  // only available on iOS because:
  // - on Android, onPress doesn't work
  // - on web, the a11y is broken
  const isGestureHandlerAvailable = Platform.select({
    ios: true,
    default: false,
  })
  const InternalTouchableOpacity: React.FC<TouchableOpacityProps & StyledProps> =
    shouldUseGestureHandler && isGestureHandlerAvailable
      ? StyledGestureTouchableOpacity
      : StyledRNTouchableOpacity

  return (
    <InternalTouchableOpacity
      isFocus={isFocus}
      onFocus={onStyledFocus}
      onBlur={onStyledBlur}
      disabled={disabled}
      {...(isWeb && !disabled ? { tabIndex: 0 } : {})}
      {...props}
      {...accessibilityAndTestId(accessibilityLabel, testID)}>
      {children}
    </InternalTouchableOpacity>
  )
}

const addStyled = (Component: typeof RNTouchableOpacity | typeof GestureTouchableOpacity) =>
  styled(Component).attrs(({ activeOpacity, theme }) => ({
    activeOpacity: activeOpacity ?? theme.activeOpacity,
  }))<StyledProps>(({ theme, unselectable, isFocus }) => ({
    userSelect: unselectable ? 'none' : 'auto',
    ...touchableFocusOutline({ theme, isFocus }),
  }))
const StyledRNTouchableOpacity = addStyled(RNTouchableOpacity)
const StyledGestureTouchableOpacity = addStyled(GestureTouchableOpacity)
